import React, {CSSProperties, ReactElement, SyntheticEvent, useCallback, useMemo} from 'react';
import './Checkbox.less';
import IconButton from "./IconButton/IconButton";
import Icons from "./IconButton/Icons";
import HelpLabel from './Help/HelpLabel';
import ErrorBoundaryO from "../ErrorBoundary/ErrorBoundaryO";

/**
 * This is just a special class of IconButton, where the icons are either checked and unchecked,
 * with a label.  No internal state here.
 */
const Checkbox = (props: CheckboxProps) => {
    const {onClick: propsOnClick, data} = props;

    const onClick = useCallback((e: SyntheticEvent) => {
        e.preventDefault();

        propsOnClick(e, data);
    }, [propsOnClick, data]);

    const renderHelp = useMemo(() => {
        return <HelpLabel message={props.helpMessage}
                          label={props.label}
                          onLabelClick={onClick}
        />;
    }, [props.helpMessage, props.label, onClick]);

    const active = props.disabled ? '' : 'Checkbox--active';
    const override = props.override ? 'Checkbox--override' : '';
    const reversed = props.reversed ? 'Checkbox--reversed' : '';
    const classes = `Checkbox ${active} ${override} ${reversed}`;

    let isChecked = false;
    if (typeof props.checked === 'string') {
        isChecked = (props.checked as string)?.toLowerCase().startsWith('t');
    }
    else if (typeof props.checked === 'boolean') isChecked = (props.checked as boolean);

    const label = useMemo(() => {
        return props.hideLabel
               ? undefined
               : props.helpMessage
                 ? renderHelp
                 : props.label;
    }, [props.hideLabel, props.helpMessage, renderHelp, props.label]);

    return (
        <ErrorBoundaryO uniqueKey={`checkbox-${props.name}`} shouldUseErrorIcon={true}>
            {/* Just a checkbox and a label */}
            <div className={classes} style={props.style}>
                {/* Checkbox */}
                <IconButton id={props.id || `Checkbox-${props.name || props.label}`}
                            autoFocus={props.autoFocus}
                            tabIndex={props.tabIndex}
                            type={props.useRadioIcon
                                  ? isChecked ? Icons.CIRCLE_CHECKED : props.indeterminate ? Icons.CIRCLEFILLED : Icons.CIRCLE
                                  : isChecked ? Icons.CHECKED : props.indeterminate ? Icons.INDETERMINATE : Icons.UNCHECKED
                            }
                            color={props.useRadioIcon ? props.indeterminate ? 'gray' : props.checked ? 'green' : '#000000aa' : undefined}
                            onClick={onClick}
                            label={label}
                            disabled={props.disabled}
                            encrypted={props.encrypted}
                            useDelay={props.useDelay}
                />
            </div>
        </ErrorBoundaryO>
    );
};

export type CheckboxClickEvent<T = any> = (e: any, data?: T) => void;

export type CheckboxProps = {
    autoFocus?: boolean;
    checked?: boolean | string;
    data?: any;
    disabled?: boolean;
    encrypted?: boolean;
    helpMessage?: string | ReactElement;
    hideLabel?: boolean;
    id?: string;
    indeterminate?: boolean;
    label?: ReactElement | string;
    name?: string; // Optional name for the button id.
    onClick: CheckboxClickEvent;
    override?: boolean;
    reversed?: boolean;
    style?: CSSProperties;
    tabIndex?: number;
    useDelay?: boolean;
    useRadioIcon?: boolean;
};

export default Checkbox;
